import React, { useRef } from 'react';
import { Bold, Italic } from 'lucide-react';
import { Input } from '../ui/input';

/**
 * Single-line input that supports `**bold**` and `*italic*` markdown,
 * with two tiny B / I buttons that wrap the current selection.
 *
 * The PDF renderer converts these markers to ReportLab's <b>/<i> tags so the
 * formatting flows through to the generated quotation PDF.
 *
 * Usage:
 *   <MarkdownInput value={t} onChange={setT} placeholder="..." />
 */
export default function MarkdownInput({ value, onChange, className = '', placeholder, testId }) {
  const inputRef = useRef(null);

  const wrap = (marker) => {
    const el = inputRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const current = value || '';

    // No selection → insert empty markers and place caret in between so the
    // user can immediately type the bold/italic text.
    if (start === end) {
      const next = current.slice(0, start) + marker + marker + current.slice(end);
      onChange(next);
      // Restore focus + caret between the markers.
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + marker.length;
        el.setSelectionRange(pos, pos);
      });
      return;
    }

    const before = current.slice(0, start);
    const sel = current.slice(start, end);
    const after = current.slice(end);
    const next = `${before}${marker}${sel}${marker}${after}`;
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + marker.length, end + marker.length);
    });
  };

  return (
    <div className="flex items-center gap-1 flex-1">
      <Input
        ref={inputRef}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
      />
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          type="button"
          onClick={() => wrap('**')}
          className="p-1 rounded hover:bg-[#F2F4EF] text-[#5C6150] hover:text-[#1A1C16] transition-colors"
          title="Bold (**text**)"
          aria-label="Bold"
          data-testid={testId ? `${testId}-bold` : undefined}
        >
          <Bold className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => wrap('*')}
          className="p-1 rounded hover:bg-[#F2F4EF] text-[#5C6150] hover:text-[#1A1C16] transition-colors"
          title="Italic (*text*)"
          aria-label="Italic"
          data-testid={testId ? `${testId}-italic` : undefined}
        >
          <Italic className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
